import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Table,
  Badge,
  Button,
  Input,
  Select,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPost } from "../lib/api";
import { useToast } from "../contexts/ToastContext";

interface Tenant {
  id: string;
  name: string;
  email: string;
  tier: "enterprise" | "pro" | "starter";
  status: "active" | "suspended" | "provisioning" | "failed" | "deprovisioned";
  deploymentCount: number;
  totalSessions: number;
  monthlySpend: number;
  createdAt: string;
  lastActive: string;
  managementMode: "managed" | "external_lab";
  lifecycleActionsSupported: boolean;
}

// current-state-2026-05-14 audit § F-011: the CP-side `/api/tenants`
// only exposes `tenant.status` and `deploymentCount` — not the latest
// deployment's status. CP measures the drift internally
// (`fleet/overview.divergenceRate`) but does not surface it on the
// tenant row. We join client-side against `/api/fleet/deployments` so
// the operator sees both the tenant-level status AND the latest
// deployment's status, and a "drift" badge when they disagree.
// Pure helpers extracted to `./tenant-drift.ts` for unit testing.

import {
  type DeploymentSummary,
  latestDeploymentFor,
  statusesDrift,
} from "./tenant-drift";

const DEPLOYMENT_STATUS_BADGE_COLOR: Record<string, "green" | "red" | "amber" | "secondary"> = {
  ready: "green",
  active: "green",
  provisioning: "amber",
  degraded: "amber",
  failed: "red",
  deprovisioned: "secondary",
  unknown: "secondary",
};

const TIER_BADGE_COLOR: Record<string, "blue" | "gray" | "secondary"> = {
  enterprise: "blue",
  pro: "gray",
  starter: "secondary",
};

const STATUS_BADGE_COLOR: Record<string, "green" | "red" | "blue" | "secondary"> = {
  active: "green",
  suspended: "red",
  provisioning: "blue",
  failed: "red",
  deprovisioned: "secondary",
};

export default function TenantManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data: tenants,
    isLoading,
    error,
  } = useQuery<Tenant[]>({
    queryKey: ["tenants"],
    queryFn: () => apiGet<{ tenants: Tenant[] }>("/api/tenants").then((d) => d.tenants),
  });

  // current-state-2026-05-14 audit § F-011: join latest deployment per
  // tenant so the table surfaces the effective deployment status. Read
  // failure degrades to empty list — the page still renders with the
  // tenant-only badge, just without the drift surface.
  const { data: deployments } = useQuery<DeploymentSummary[]>({
    queryKey: ["fleet", "deployments-for-tenants"],
    queryFn: async () => {
      const data = await apiGet<DeploymentSummary[] | { deployments?: DeploymentSummary[] }>(
        "/api/fleet/deployments",
      );
      return Array.isArray(data) ? data : (data.deployments ?? []);
    },
  });

  usePolling([["tenants"], ["fleet", "deployments-for-tenants"]], 30_000);

  const suspendMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "suspend" | "resume" }) =>
      apiPost(`/api/tenants/${id}/${action}`),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success(vars.action === "suspend" ? "Tenant suspended" : "Tenant resumed");
    },
    onError: (err: Error) => toast.error("Action failed", err.message),
  });

  const filtered = (tenants ?? []).filter((t) => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (tierFilter !== "all" && t.tier !== tierFilter) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    return true;
  });

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (row: Tenant) => (
        <span className="font-medium" style={{ color: "var(--c-text)" }}>
          {row.name}
        </span>
      ),
    },
    { key: "email", header: "Email" },
    {
      key: "tier",
      header: "Tier",
      render: (row: Tenant) => <Badge color={TIER_BADGE_COLOR[row.tier]}>{row.tier}</Badge>,
    },
    {
      key: "status",
      header: "Status",
      render: (row: Tenant) => {
        const latest = latestDeploymentFor(row.id, deployments ?? []);
        const drift = statusesDrift(row.status, latest?.status);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
            <Badge color={STATUS_BADGE_COLOR[row.status]} dot>
              {row.status}
            </Badge>
            {latest ? (
              <Badge
                color={DEPLOYMENT_STATUS_BADGE_COLOR[latest.status] ?? "secondary"}
                title={`Latest deployment ${latest.id}: ${latest.status}`}
              >
                deploy: {latest.status}
              </Badge>
            ) : null}
            {drift ? (
              <Badge color="amber" title="Tenant marked active but latest deployment is not running">
                drift
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "managementMode",
      header: "Management",
      render: (row: Tenant) => (
        <Badge color={row.managementMode === "external_lab" ? "amber" : "green"}>
          {row.managementMode === "external_lab" ? "External lab sync" : "Managed"}
        </Badge>
      ),
    },
    {
      key: "deploymentCount",
      header: "Deployments",
    },
    {
      key: "monthlySpend",
      header: "Monthly Spend",
      render: (row: Tenant) => (
        <span style={{ color: "var(--c-text)" }}>${row.monthlySpend.toLocaleString()}</span>
      ),
    },
    {
      key: "lastActive",
      header: "Last Active",
      render: (row: Tenant) => new Date(row.lastActive).toLocaleDateString(),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row: Tenant) => (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => navigate(`/tenants/${row.id}`)}>
            View
          </Button>
          {row.lifecycleActionsSupported &&
            (row.status === "active" || row.status === "suspended") && (
              <Button
                size="sm"
                variant={row.status === "active" ? "danger" : "primary"}
                onClick={() =>
                  suspendMutation.mutate({
                    id: row.id,
                    action: row.status === "active" ? "suspend" : "resume",
                  })
                }
              >
                {row.status === "active" ? "Suspend" : "Resume"}
              </Button>
            )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Tenant Management" subtitle="Manage your platform tenants" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !tenants) {
    return (
      <div>
        <PageHeader title="Tenant Management" subtitle="Manage your platform tenants" />
        <QueryError error={error} queryKeys={[["tenants"]]} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Tenant Management" subtitle="Manage your platform tenants" />

      <Card className="mb-6">
        <div className="space-y-2 text-sm">
          <div className="font-medium" style={{ color: "var(--c-text)" }}>
            Tenant provisioning is stocktaked in Adam for now.
          </div>
          <div style={{ color: "var(--c-text-secondary)", lineHeight: 1.5 }}>
            The current staging environment uses externally synced prod-mirror deployments, not the
            final VM-first operator provisioning path. Creation and lifecycle controls stay hidden
            until Adam owns that workflow end to end.
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="w-56">
          <Input
            placeholder="Search tenants by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            label="Tier"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            options={[
              { value: "all", label: "All tiers" },
              { value: "enterprise", label: "Enterprise" },
              { value: "pro", label: "Pro" },
              { value: "starter", label: "Starter" },
            ]}
          />
        </div>
        <div className="w-40">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "suspended", label: "Suspended" },
              { value: "provisioning", label: "Provisioning" },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <Card padding={false}>
        <Table
          columns={columns}
          data={filtered as unknown as Record<string, unknown>[]}
          keyField="id"
          emptyMessage="No tenants found"
        />
      </Card>
    </div>
  );
}
